import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { swal } from '../utils/swal';
import './Operadores.css';

interface Vehiculo {
  id: number;
  placa: string;
  numero_interno?: string;
  marca?: string;
  modelo?: string;
  año?: number;
  color?: string;
  numero_motor?: string;
  numero_chasis?: string;
  tipo_combustible?: string;
  capacidad_tanque?: number;
  tipo_vehiculo?: string;
  repotenciado?: boolean;
  configuracion?: string;
  capacidad_carga?: number;
  carroceria?: string;
  peso_vacio?: number;
  estado: string;
  operador_id?: number;
  operador_nombre?: string;
  operador_documento?: string;
  operador_celular?: string | null;
  propietario_nombre?: string;
  documentos_vehiculo?: {
    soat_numero?: string;
    soat_vigencia?: string;
    soat_aseguradora?: string;
    poliza_resp_civil_numero?: string;
    poliza_resp_civil_vigencia?: string;
    poliza_resp_civil_aseguradora?: string;
    tecnomecanica_numero?: string;
    tecnomecanica_vigencia?: string;
    tarjeta_propiedad_numero?: string;
    tarjeta_propiedad_vigencia?: string;
  };
}

const Vehiculos: React.FC = () => {
  const [vehiculos, setVehiculos] = useState<Vehiculo[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<string>('');
  const [filtroTipo, setFiltroTipo] = useState<string>('');
  const [filtroOperador, setFiltroOperador] = useState<string>('');
  const [showCrearModal, setShowCrearModal] = useState(false);
  const [showEditarModal, setShowEditarModal] = useState(false);
  const [showVerModal, setShowVerModal] = useState(false);
  const [showAsignarModal, setShowAsignarModal] = useState(false);
  const [vehiculoSeleccionado, setVehiculoSeleccionado] = useState<Vehiculo | null>(null);
  const [operadores, setOperadores] = useState<any[]>([]);
  const [operadorSeleccionado, setOperadorSeleccionado] = useState<string>('');
  const [documentosVehiculo, setDocumentosVehiculo] = useState<any[]>([]);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);
  const [formData, setFormData] = useState({
    placa: '',
    numero_interno: '',
    marca: '',
    modelo: '',
    año: '',
    color: '',
    numero_motor: '',
    numero_chasis: '',
    tipo_combustible: 'Diesel',
    capacidad_tanque: '',
    tipo_vehiculo: '',
    repotenciado: false,
    configuracion: '',
    capacidad_carga: '',
    carroceria: '',
    peso_vacio: '',
    estado: 'activo',
    // Documentos y pólizas
    soat_numero: '',
    soat_vigencia: '',
    soat_aseguradora: '',
    poliza_resp_civil_numero: '',
    poliza_resp_civil_vigencia: '',
    poliza_resp_civil_aseguradora: '',
    tecnomecanica_numero: '',
    tecnomecanica_vigencia: '',
    tarjeta_propiedad_numero: '',
    tarjeta_propiedad_vigencia: ''
  });

  useEffect(() => {
    loadVehiculos();
  }, [search, filtroEstado, filtroTipo, filtroOperador]);

  const loadVehiculos = async () => {
    try {
      setLoading(true);
      const params: any = { limit: 1000 };
      if (search) params.search = search;
      if (filtroEstado) params.estado = filtroEstado;
      if (filtroTipo) params.tipo_vehiculo = filtroTipo;
      if (filtroOperador) params.operador_id = filtroOperador;
      
      const response = await api.get('/vehiculos', { params });
      let vehiculosData = response.data.data || [];
      
      // Log para debugging - respuesta completa
      console.log('🔍 Respuesta completa del API:', {
        total: vehiculosData.length,
        estructura: vehiculosData.length > 0 ? Object.keys(vehiculosData[0]) : []
      });
      
      const vehiculoABC122 = vehiculosData.find((v: Vehiculo) => v.placa === 'ABC122');
      if (vehiculoABC122) {
        console.log('🔍 Vehículo ABC122 recibido RAW:', vehiculoABC122);
        console.log('🔍 Vehículo ABC122 - operador_id:', vehiculoABC122.operador_id, 'tipo:', typeof vehiculoABC122.operador_id);
        console.log('🔍 Vehículo ABC122 - operador_nombre:', vehiculoABC122.operador_nombre, 'tipo:', typeof vehiculoABC122.operador_nombre);
        console.log('🔍 Vehículo ABC122 - todas las keys:', Object.keys(vehiculoABC122));
        console.log('🔍 Vehículo ABC122 - keys con "operador":', Object.keys(vehiculoABC122).filter(k => k.includes('operador')));
        console.log('🔍 Vehículo ABC122 - tiene documentos_vehiculo:', !!vehiculoABC122.documentos_vehiculo);
        if (vehiculoABC122.documentos_vehiculo) {
          console.log('🔍 Vehículo ABC122 - documentos_vehiculo:', vehiculoABC122.documentos_vehiculo);
        }
      } else {
        console.log('⚠️ ABC122 NO encontrado en respuesta del API');
      }
      
      // Limpiar espacios en blanco del nombre del operador y asegurar tipos
      vehiculosData = vehiculosData.map((v: Vehiculo) => {
        const vehiculo = {
          ...v,
          operador_id: v.operador_id ? parseInt(String(v.operador_id)) : null,
          operador_nombre: v.operador_nombre ? String(v.operador_nombre).trim() : null,
          operador_documento: v.operador_documento ? String(v.operador_documento) : null,
          operador_celular: v.operador_celular ? String(v.operador_celular) : null
        };
        
        // Log para ABC122 después de procesar
        if (vehiculo.placa === 'ABC122') {
          console.log('🔍 ABC122 después de procesar:', {
            operador_id: vehiculo.operador_id,
            operador_nombre: vehiculo.operador_nombre,
            tipo_operador_id: typeof vehiculo.operador_id,
            tiene_documentos_vehiculo: !!vehiculo.documentos_vehiculo,
            documentos_vehiculo: vehiculo.documentos_vehiculo
          });
        }
        
        return vehiculo;
      });
      
      // Filtrar por tipo de vehículo en el frontend si es necesario
      if (filtroTipo && !params.tipo_vehiculo) {
        vehiculosData = vehiculosData.filter((v: Vehiculo) => v.tipo_vehiculo === filtroTipo);
      }
      
      // Filtrar por operador en el frontend
      if (filtroOperador === 'sin_asignar') {
        vehiculosData = vehiculosData.filter((v: Vehiculo) => !v.operador_id);
      } else if (filtroOperador === 'con_asignar') {
        vehiculosData = vehiculosData.filter((v: Vehiculo) => v.operador_id);
      }
      
      setVehiculos(vehiculosData);
    } catch (error) {
      console.error('Error cargando vehículos:', error);
      await swal.error('Error al cargar vehículos');
    } finally {
      setLoading(false);
    }
  };

  const loadOperadores = async () => {
    try {
      const response = await api.get('/operadores', {
        params: { limit: 1000, estado: 'aprobado' }
      });
      setOperadores(response.data.data || []);
    } catch (error) {
      console.error('Error cargando operadores:', error);
    }
  };

  const abrirCrearVehiculo = () => {
    setFormData({
      placa: '',
      numero_interno: '',
      marca: '',
      modelo: '',
      año: '',
      color: '',
      numero_motor: '',
      numero_chasis: '',
      tipo_combustible: 'Diesel',
      capacidad_tanque: '',
      tipo_vehiculo: '',
      repotenciado: false,
      configuracion: '',
      capacidad_carga: '',
      carroceria: '',
      peso_vacio: '',
      estado: 'activo',
      soat_numero: '',
      soat_vigencia: '',
      soat_aseguradora: '',
      poliza_resp_civil_numero: '',
      poliza_resp_civil_vigencia: '',
      poliza_resp_civil_aseguradora: '',
      tecnomecanica_numero: '',
      tecnomecanica_vigencia: '',
      tarjeta_propiedad_numero: '',
      tarjeta_propiedad_vigencia: ''
    });
    setShowCrearModal(true);
  };

  const abrirEditarVehiculo = async (vehiculo: Vehiculo) => {
    try {
      // Cargar datos completos del vehículo para obtener documentos_vehiculo
      const response = await api.get(`/vehiculos/${vehiculo.id}`);
      const vehiculoCompleto = response.data;
      
      setVehiculoSeleccionado(vehiculoCompleto);
      setFormData({
        placa: vehiculoCompleto.placa,
        numero_interno: vehiculoCompleto.numero_interno || '',
        marca: vehiculoCompleto.marca || '',
        modelo: vehiculoCompleto.modelo || '',
        año: vehiculoCompleto.año?.toString() || '',
        color: vehiculoCompleto.color || '',
        numero_motor: vehiculoCompleto.numero_motor || '',
        numero_chasis: vehiculoCompleto.numero_chasis || '',
        tipo_combustible: vehiculoCompleto.tipo_combustible || 'Diesel',
        capacidad_tanque: vehiculoCompleto.capacidad_tanque?.toString() || '',
        tipo_vehiculo: vehiculoCompleto.tipo_vehiculo || '',
        repotenciado: vehiculoCompleto.repotenciado || false,
        configuracion: vehiculoCompleto.configuracion || '',
        capacidad_carga: vehiculoCompleto.capacidad_carga?.toString() || '',
        carroceria: vehiculoCompleto.carroceria || '',
        peso_vacio: vehiculoCompleto.peso_vacio?.toString() || '',
        estado: vehiculoCompleto.estado || 'activo',
        // Documentos y pólizas
        soat_numero: vehiculoCompleto.documentos_vehiculo?.soat_numero || '',
        soat_vigencia: vehiculoCompleto.documentos_vehiculo?.soat_vigencia || '',
        soat_aseguradora: vehiculoCompleto.documentos_vehiculo?.soat_aseguradora || '',
        poliza_resp_civil_numero: vehiculoCompleto.documentos_vehiculo?.poliza_resp_civil_numero || '',
        poliza_resp_civil_vigencia: vehiculoCompleto.documentos_vehiculo?.poliza_resp_civil_vigencia || '',
        poliza_resp_civil_aseguradora: vehiculoCompleto.documentos_vehiculo?.poliza_resp_civil_aseguradora || '',
        tecnomecanica_numero: vehiculoCompleto.documentos_vehiculo?.tecnomecanica_numero || '',
        tecnomecanica_vigencia: vehiculoCompleto.documentos_vehiculo?.tecnomecanica_vigencia || '',
        tarjeta_propiedad_numero: vehiculoCompleto.documentos_vehiculo?.tarjeta_propiedad_numero || '',
        tarjeta_propiedad_vigencia: vehiculoCompleto.documentos_vehiculo?.tarjeta_propiedad_vigencia || ''
      });
      setShowEditarModal(true);
    } catch (error) {
      await swal.error('Error al cargar datos del vehículo');
    }
  };

  const abrirVerVehiculo = async (vehiculo: Vehiculo) => {
    try {
      setLoadingDocumentos(true);
      // Cargar datos completos del vehículo
      const response = await api.get(`/vehiculos/${vehiculo.id}`);
      setVehiculoSeleccionado(response.data);
      
      // Cargar documentos del vehículo
      try {
        const docsResponse = await api.get('/documentos', {
          params: {
            entidad_tipo: 'vehiculo',
            entidad_id: vehiculo.id
          }
        });
        setDocumentosVehiculo(docsResponse.data.data || []);
      } catch (error) {
        console.error('Error cargando documentos:', error);
        setDocumentosVehiculo([]);
      }
      
      setShowVerModal(true);
    } catch (error) {
      await swal.error('Error al cargar datos del vehículo');
    } finally {
      setLoadingDocumentos(false);
    }
  };

  // Función para calcular días hasta vencimiento y estado
  const calcularEstadoVencimiento = (fechaVencimiento: string | null) => {
    if (!fechaVencimiento || !fechaVencimiento.trim()) {
      return { estado: 'sin_fecha', dias: null, mensaje: 'Sin fecha de vencimiento' };
    }
    
    // Parsear la fecha de forma segura (formato YYYY-MM-DD)
    const fechaParts = fechaVencimiento.trim().split('-');
    if (fechaParts.length !== 3) {
      console.warn(`⚠️ Formato de fecha inválido: ${fechaVencimiento}`);
      return { estado: 'sin_fecha', dias: null, mensaje: 'Fecha inválida' };
    }
    
    const año = parseInt(fechaParts[0], 10);
    const mes = parseInt(fechaParts[1], 10) - 1; // Mes es 0-indexed en Date
    const dia = parseInt(fechaParts[2], 10);
    
    if (isNaN(año) || isNaN(mes) || isNaN(dia)) {
      console.warn(`⚠️ Fecha con valores inválidos: ${fechaVencimiento}`);
      return { estado: 'sin_fecha', dias: null, mensaje: 'Fecha inválida' };
    }
    
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    const vencimiento = new Date(año, mes, dia);
    vencimiento.setHours(0, 0, 0, 0);
    
    // Validar que la fecha sea válida
    if (vencimiento.getFullYear() !== año || vencimiento.getMonth() !== mes || vencimiento.getDate() !== dia) {
      console.warn(`⚠️ Fecha inválida después de parsear: ${fechaVencimiento}`);
      return { estado: 'sin_fecha', dias: null, mensaje: 'Fecha inválida' };
    }
    
    const diasRestantes = Math.ceil((vencimiento.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
    
    console.log(`📅 Fecha: ${fechaVencimiento}, Hoy: ${hoy.toISOString().split('T')[0]}, Días restantes: ${diasRestantes}`);
    
    if (diasRestantes < 0) {
      return { 
        estado: 'vencido', 
        dias: Math.abs(diasRestantes), 
        mensaje: `Vencido hace ${Math.abs(diasRestantes)} día(s)`,
        color: '#dc3545',
        bgColor: '#fee2e2'
      };
    } else if (diasRestantes <= 30) {
      return { 
        estado: 'por_vencer', 
        dias: diasRestantes, 
        mensaje: `Vence en ${diasRestantes} día(s)`,
        color: '#f59e0b',
        bgColor: '#fef3c7'
      };
    } else {
      return { 
        estado: 'vigente', 
        dias: diasRestantes, 
        mensaje: `Vigente (${diasRestantes} días restantes)`,
        color: '#10b981',
        bgColor: '#d1fae5'
      };
    }
  };

  // Función para calcular el estado general de los documentos del vehículo
  // IMPORTANTE: Solo usa datos de documentos_vehiculo (pólizas, SOAT, etc.) NO documentos subidos como archivos
  const calcularEstadoDocumentosVehiculo = (vehiculo: Vehiculo) => {
    console.log(`🔍 [calcularEstadoDocumentosVehiculo] Vehículo ${vehiculo.placa}:`, {
      tiene_documentos_vehiculo: !!vehiculo.documentos_vehiculo,
      documentos_vehiculo: vehiculo.documentos_vehiculo
    });
    
    if (!vehiculo.documentos_vehiculo) {
      console.log(`⚠️ [calcularEstadoDocumentosVehiculo] Vehículo ${vehiculo.placa} NO tiene documentos_vehiculo`);
      return { estado: 'sin_datos', mensaje: 'Sin datos', color: '#6c757d', bgColor: '#f3f4f6', icon: '❓' };
    }

    const documentos = vehiculo.documentos_vehiculo;
    
    // Solo usar fechas de vigencia de pólizas y documentos (NO documentos subidos como archivos)
    const fechasVigencia = [
      documentos.soat_vigencia,
      documentos.poliza_resp_civil_vigencia,
      documentos.tecnomecanica_vigencia,
      documentos.tarjeta_propiedad_vigencia
    ].filter((f): f is string => !!f && f.trim() !== ''); // Filtrar fechas nulas, vacías o solo espacios

    console.log(`🔍 [calcularEstadoDocumentosVehiculo] Vehículo ${vehiculo.placa} fechas encontradas:`, fechasVigencia);

    if (fechasVigencia.length === 0) {
      console.log(`⚠️ [calcularEstadoDocumentosVehiculo] Vehículo ${vehiculo.placa} tiene documentos_vehiculo pero sin fechas de vigencia`);
      return { estado: 'sin_fechas', mensaje: 'Sin fechas', color: '#6c757d', bgColor: '#f3f4f6', icon: '❓' };
    }

    let tieneVencidos = false;
    let tienePorVencer = false;

    fechasVigencia.forEach(fecha => {
      const estado = calcularEstadoVencimiento(fecha || null);
      console.log(`  - Fecha ${fecha}: estado=${estado.estado}, días=${estado.dias}`);
      if (estado.estado === 'vencido') {
        tieneVencidos = true;
      } else if (estado.estado === 'por_vencer') {
        tienePorVencer = true;
      }
    });

    if (tieneVencidos) {
      console.log(`❌ [calcularEstadoDocumentosVehiculo] Vehículo ${vehiculo.placa} tiene documentos VENCIDOS`);
      return { 
        estado: 'vencido', 
        mensaje: '⚠️ Documentos vencidos', 
        color: '#dc3545', 
        bgColor: '#fee2e2',
        icon: '❌'
      };
    } else if (tienePorVencer) {
      console.log(`⚠️ [calcularEstadoDocumentosVehiculo] Vehículo ${vehiculo.placa} tiene documentos POR VENCER`);
      return { 
        estado: 'por_vencer', 
        mensaje: '⏰ Por vencer', 
        color: '#f59e0b', 
        bgColor: '#fef3c7',
        icon: '⚠️'
      };
    } else {
      console.log(`✅ [calcularEstadoDocumentosVehiculo] Vehículo ${vehiculo.placa} TODO OK`);
      return { 
        estado: 'ok', 
        mensaje: '✅ Todo OK', 
        color: '#10b981', 
        bgColor: '#d1fae5',
        icon: '✅'
      };
    }
  };

  const abrirAsignarVehiculo = async (vehiculo: Vehiculo) => {
    setVehiculoSeleccionado(vehiculo);
    await loadOperadores();
    setOperadorSeleccionado(vehiculo.operador_id?.toString() || '');
    setShowAsignarModal(true);
  };

  const guardarVehiculo = async () => {
    try {
      console.log('🔍 Guardando vehículo, formData:', formData);
      console.log('🔍 showCrearModal:', showCrearModal, 'showEditarModal:', showEditarModal);
      console.log('🔍 vehiculoSeleccionado:', vehiculoSeleccionado);
      
      const datos: any = {
        numero_interno: formData.numero_interno || null,
        marca: formData.marca || null,
        modelo: formData.modelo || null,
        año: formData.año ? parseInt(formData.año) : null,
        color: formData.color || null,
        numero_motor: formData.numero_motor || null,
        numero_chasis: formData.numero_chasis || null,
        tipo_combustible: formData.tipo_combustible || 'Diesel',
        capacidad_tanque: formData.capacidad_tanque ? parseFloat(formData.capacidad_tanque) : null,
        tipo_vehiculo: formData.tipo_vehiculo || null,
        repotenciado: formData.repotenciado || false,
        configuracion: formData.configuracion || null,
        capacidad_carga: formData.capacidad_carga ? parseFloat(formData.capacidad_carga) : null,
        carroceria: formData.carroceria || null,
        peso_vacio: formData.peso_vacio ? parseFloat(formData.peso_vacio) : null,
        estado: formData.estado || 'activo',
        propietario_id: null, // Por ahora null, se puede agregar después
        // Documentos y pólizas
        documentos_vehiculo: {
          soat_numero: formData.soat_numero || null,
          soat_vigencia: formData.soat_vigencia || null,
          soat_aseguradora: formData.soat_aseguradora || null,
          poliza_resp_civil_numero: formData.poliza_resp_civil_numero || null,
          poliza_resp_civil_vigencia: formData.poliza_resp_civil_vigencia || null,
          poliza_resp_civil_aseguradora: formData.poliza_resp_civil_aseguradora || null,
          tecnomecanica_numero: formData.tecnomecanica_numero || null,
          tecnomecanica_vigencia: formData.tecnomecanica_vigencia || null,
          tarjeta_propiedad_numero: formData.tarjeta_propiedad_numero || null,
          tarjeta_propiedad_vigencia: formData.tarjeta_propiedad_vigencia || null
        }
      };

      if (showCrearModal) {
        // Agregar placa solo para crear
        datos.placa = formData.placa;
        console.log('🔍 Creando vehículo con datos:', datos);
        await api.post('/vehiculos', datos);
        await swal.success('Vehículo creado exitosamente');
      } else if (vehiculoSeleccionado) {
        console.log('🔍 Actualizando vehículo ID:', vehiculoSeleccionado.id, 'con datos:', datos);
        const response = await api.put(`/vehiculos/${vehiculoSeleccionado.id}`, datos);
        console.log('🔍 Respuesta de actualización:', response.data);
        await swal.success('Vehículo actualizado exitosamente');
      }

      setShowCrearModal(false);
      setShowEditarModal(false);
      setVehiculoSeleccionado(null);
      // Esperar un momento antes de recargar para asegurar que la BD se actualizó
      setTimeout(() => {
        loadVehiculos();
      }, 300);
    } catch (error: any) {
      console.error('❌ Error al guardar vehículo:', error);
      console.error('❌ Error response:', error.response);
      await swal.error(error.response?.data?.error || error.response?.data?.details || 'Error al guardar vehículo');
    }
  };

  const eliminarVehiculo = async (vehiculo: Vehiculo) => {
    const result = await swal.confirm(
      `¿Está seguro de eliminar el vehículo ${vehiculo.placa}?`,
      'Esta acción no se puede deshacer'
    );

    if (result.isConfirmed) {
      try {
        await api.delete(`/vehiculos/${vehiculo.id}`);
        await swal.success('Vehículo eliminado exitosamente');
        loadVehiculos();
      } catch (error: any) {
        await swal.error(error.response?.data?.error || 'Error al eliminar vehículo');
      }
    }
  };

  const asignarVehiculo = async () => {
    if (!vehiculoSeleccionado) return;

    try {
      const operadorId = operadorSeleccionado ? parseInt(operadorSeleccionado) : null;

      // Si hay un operador previo, quitarle el vehículo
      if (vehiculoSeleccionado.operador_id) {
        await api.put(`/operadores/${vehiculoSeleccionado.operador_id}/vehiculo`, {
          vehiculo_id: null
        });
      }

      // Asignar a nuevo operador si se seleccionó uno
      if (operadorId) {
        await api.put(`/operadores/${operadorId}/vehiculo`, {
          vehiculo_id: vehiculoSeleccionado.id
        });
      }

      await swal.success('Vehículo asignado correctamente');
      setShowAsignarModal(false);
      setVehiculoSeleccionado(null);
      setOperadorSeleccionado('');
      // Esperar un momento antes de recargar para asegurar que la BD se actualizó
      setTimeout(() => {
        loadVehiculos();
      }, 500);
    } catch (error: any) {
      await swal.error(error.response?.data?.error || 'Error al asignar vehículo');
    }
  };

  return (
    <div className="operadores-page">
      <div className="page-header">
        <div className="header-actions">
          <input
            type="text"
            placeholder="Buscar por placa o número interno..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && loadVehiculos()}
            className="search-input"
          />
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="search-input"
            style={{ flex: '0 0 auto', minWidth: '150px' }}
          >
            <option value="">Todos los estados</option>
            <option value="activo">Activo</option>
            <option value="inactivo">Inactivo</option>
            <option value="mantenimiento">Mantenimiento</option>
            <option value="baja">Baja</option>
          </select>
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className="search-input"
            style={{ flex: '0 0 auto', minWidth: '150px' }}
          >
            <option value="">Todos los tipos</option>
            <option value="TRACTOCAMION">Tractocamión</option>
            <option value="CAMION">Camión</option>
            <option value="CAMIONETA">Camioneta</option>
            <option value="OTRO">Otro</option>
          </select>
          <select
            value={filtroOperador}
            onChange={(e) => setFiltroOperador(e.target.value)}
            className="search-input"
            style={{ flex: '0 0 auto', minWidth: '180px' }}
          >
            <option value="">Todos los operadores</option>
            <option value="sin_asignar">Sin asignar</option>
            <option value="con_asignar">Con operador asignado</option>
          </select>
          <button onClick={loadVehiculos} className="btn-primary">
            🔍 Buscar
          </button>
          {(search || filtroEstado || filtroTipo || filtroOperador) && (
            <button
              onClick={() => {
                setSearch('');
                setFiltroEstado('');
                setFiltroTipo('');
                setFiltroOperador('');
              }}
              className="btn-primary"
              style={{ background: '#6c757d' }}
            >
              Limpiar
            </button>
          )}
          <button onClick={abrirCrearVehiculo} className="btn-primary">
            ➕ Nuevo Vehículo
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Cargando vehículos...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Placa</th>
                <th>Número Interno</th>
                <th>Marca</th>
                <th>Modelo</th>
                <th>Año</th>
                <th>Tipo</th>
                <th>Operador Asignado</th>
                <th>Estado Documentos</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {vehiculos.length === 0 ? (
                <tr>
                  <td colSpan={10} className="empty-state">
                    No hay vehículos registrados
                  </td>
                </tr>
              ) : (
                vehiculos.map((vehiculo) => {
                  const estadoDocs = calcularEstadoDocumentosVehiculo(vehiculo);
                  return (
                    <tr key={vehiculo.id}>
                      <td>
                        <strong style={{ color: '#0f4222', fontSize: '1.05em' }}>{vehiculo.placa}</strong>
                      </td>
                      <td>{vehiculo.numero_interno || '-'}</td>
                      <td>{vehiculo.marca || '-'}</td>
                      <td>{vehiculo.modelo || '-'}</td>
                      <td>{vehiculo.año || '-'}</td>
                      <td>
                        {vehiculo.tipo_vehiculo ? (
                          <span className="badge badge-info">
                            {vehiculo.tipo_vehiculo}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        {vehiculo.operador_id && vehiculo.operador_nombre ? (
                          <div>
                            <div style={{ fontWeight: '500', marginBottom: '2px' }}>
                              {vehiculo.operador_nombre.trim()}
                            </div>
                            {vehiculo.operador_documento && (
                              <div style={{ fontSize: '0.85em', color: '#64748b' }}>
                                CC: {vehiculo.operador_documento}
                              </div>
                            )}
                            {vehiculo.operador_celular && (
                              <div style={{ fontSize: '0.85em', color: '#64748b', marginTop: '2px' }}>
                                📱 {vehiculo.operador_celular}
                              </div>
                            )}
                          </div>
                        ) : vehiculo.operador_id ? (
                          <div>
                            <div style={{ fontWeight: '500', marginBottom: '2px', color: '#f59e0b' }}>
                              Operador ID: {vehiculo.operador_id}
                            </div>
                            <div style={{ fontSize: '0.85em', color: '#94a3b8' }}>
                              (Nombre no disponible)
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Sin asignar</span>
                        )}
                      </td>
                      <td>
                        <span 
                          className="badge" 
                          style={{ 
                            backgroundColor: estadoDocs.bgColor, 
                            color: estadoDocs.color,
                            fontWeight: 'bold',
                            padding: '6px 12px',
                            borderRadius: '4px'
                          }}
                        >
                          {estadoDocs.icon} {estadoDocs.mensaje}
                        </span>
                      </td>
                      <td>
                        {vehiculo.estado === 'activo' ? (
                          <span className="badge badge-success">✅ Activo</span>
                        ) : vehiculo.estado === 'inactivo' ? (
                          <span className="badge badge-secondary">⏸️ Inactivo</span>
                        ) : vehiculo.estado === 'mantenimiento' ? (
                          <span className="badge badge-warning">🔧 Mantenimiento</span>
                        ) : vehiculo.estado === 'baja' ? (
                          <span className="badge badge-danger">❌ Baja</span>
                        ) : (
                          <span className="badge badge-secondary">{vehiculo.estado}</span>
                        )}
                      </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => abrirVerVehiculo(vehiculo)}
                          className="btn-link"
                          title="Ver detalles"
                        >
                          👁️ Ver
                        </button>
                        <button
                          onClick={() => abrirEditarVehiculo(vehiculo)}
                          className="btn-link"
                          title="Editar"
                          style={{ fontWeight: '600' }}
                        >
                          ✏️ Editar
                        </button>
                        <button
                          onClick={() => abrirAsignarVehiculo(vehiculo)}
                          className="btn-link"
                          title="Asignar a operador"
                        >
                          👤 Asignar
                        </button>
                        <button
                          onClick={() => eliminarVehiculo(vehiculo)}
                          className="btn-link"
                          style={{ color: '#dc3545' }}
                          title="Eliminar"
                        >
                          🗑️ Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Crear/Editar */}
      {(showCrearModal || showEditarModal) && (
        <div className="modal-overlay" onClick={() => {
          setShowCrearModal(false);
          setShowEditarModal(false);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{showCrearModal ? 'Nuevo Vehículo' : 'Editar Vehículo'}</h2>
              <button onClick={() => {
                setShowCrearModal(false);
                setShowEditarModal(false);
              }}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Placa *</label>
                  <input
                    type="text"
                    value={formData.placa}
                    onChange={(e) => setFormData({ ...formData, placa: e.target.value.toUpperCase() })}
                    required
                    disabled={showEditarModal}
                  />
                </div>
                <div className="form-group">
                  <label>Número Interno</label>
                  <input
                    type="text"
                    value={formData.numero_interno}
                    onChange={(e) => setFormData({ ...formData, numero_interno: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Marca *</label>
                  <input
                    type="text"
                    value={formData.marca}
                    onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Modelo *</label>
                  <input
                    type="text"
                    value={formData.modelo}
                    onChange={(e) => setFormData({ ...formData, modelo: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Año</label>
                  <input
                    type="number"
                    value={formData.año}
                    onChange={(e) => setFormData({ ...formData, año: e.target.value })}
                    min="1900"
                    max={new Date().getFullYear() + 1}
                  />
                </div>
                <div className="form-group">
                  <label>Color</label>
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Tipo de Vehículo</label>
                  <select
                    value={formData.tipo_vehiculo}
                    onChange={(e) => setFormData({ ...formData, tipo_vehiculo: e.target.value })}
                  >
                    <option value="">Seleccione...</option>
                    <option value="TRACTOCAMION">Tractocamión</option>
                    <option value="CAMION">Camión</option>
                    <option value="CAMIONETA">Camioneta</option>
                    <option value="OTRO">Otro</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.repotenciado}
                      onChange={(e) => setFormData({ ...formData, repotenciado: e.target.checked })}
                    />
                    {' '}Repotenciado
                  </label>
                </div>
                <div className="form-group">
                  <label>Configuración</label>
                  <input
                    type="text"
                    value={formData.configuracion}
                    onChange={(e) => setFormData({ ...formData, configuracion: e.target.value })}
                    placeholder="Ej: 383, 4x2"
                  />
                </div>
                <div className="form-group">
                  <label>Capacidad de Carga (kg)</label>
                  <input
                    type="number"
                    value={formData.capacidad_carga}
                    onChange={(e) => setFormData({ ...formData, capacidad_carga: e.target.value })}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label>Carrocería</label>
                  <input
                    type="text"
                    value={formData.carroceria}
                    onChange={(e) => setFormData({ ...formData, carroceria: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Peso Vacío (kg)</label>
                  <input
                    type="number"
                    value={formData.peso_vacio}
                    onChange={(e) => setFormData({ ...formData, peso_vacio: e.target.value })}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label>Número de Motor</label>
                  <input
                    type="text"
                    value={formData.numero_motor}
                    onChange={(e) => setFormData({ ...formData, numero_motor: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Número de Chasis</label>
                  <input
                    type="text"
                    value={formData.numero_chasis}
                    onChange={(e) => setFormData({ ...formData, numero_chasis: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Tipo de Combustible</label>
                  <select
                    value={formData.tipo_combustible}
                    onChange={(e) => setFormData({ ...formData, tipo_combustible: e.target.value })}
                  >
                    <option value="Diesel">Diesel</option>
                    <option value="Gasolina">Gasolina</option>
                    <option value="Gas">Gas</option>
                    <option value="Electrico">Eléctrico</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Capacidad del Tanque (Litros)</label>
                  <input
                    type="number"
                    value={formData.capacidad_tanque}
                    onChange={(e) => setFormData({ ...formData, capacidad_tanque: e.target.value })}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label>Estado</label>
                  <select
                    value={formData.estado}
                    onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                  >
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                    <option value="mantenimiento">Mantenimiento</option>
                    <option value="baja">Baja</option>
                  </select>
                </div>
              </div>
              
              {/* Sección de Documentos y Pólizas */}
              <div className="form-section" style={{ marginTop: '30px', borderTop: '2px solid #0f4222', paddingTop: '20px' }}>
                <h3 style={{ marginBottom: '15px', color: '#2c3e50', borderBottom: '2px solid #0f4222', paddingBottom: '10px' }}>
                  📄 DOCUMENTOS Y PÓLIZAS
                </h3>
                <div className="form-grid">
                  {/* SOAT */}
                  <div className="form-group">
                    <label>SOAT - Número</label>
                    <input
                      type="text"
                      value={formData.soat_numero}
                      onChange={(e) => setFormData({ ...formData, soat_numero: e.target.value })}
                      placeholder="Ej: 25080043613810"
                    />
                  </div>
                  <div className="form-group">
                    <label>SOAT - Vigencia</label>
                    <input
                      type="date"
                      value={formData.soat_vigencia}
                      onChange={(e) => setFormData({ ...formData, soat_vigencia: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>SOAT - Aseguradora</label>
                    <input
                      type="text"
                      value={formData.soat_aseguradora}
                      onChange={(e) => setFormData({ ...formData, soat_aseguradora: e.target.value })}
                      placeholder="Ej: 860002400"
                    />
                  </div>

                  {/* Póliza de Responsabilidad Civil */}
                  <div className="form-group">
                    <label>Póliza Resp. Civil - Número</label>
                    <input
                      type="text"
                      value={formData.poliza_resp_civil_numero}
                      onChange={(e) => setFormData({ ...formData, poliza_resp_civil_numero: e.target.value })}
                      placeholder="Ej: BCH20003126"
                    />
                  </div>
                  <div className="form-group">
                    <label>Póliza Resp. Civil - Vigencia</label>
                    <input
                      type="date"
                      value={formData.poliza_resp_civil_vigencia}
                      onChange={(e) => setFormData({ ...formData, poliza_resp_civil_vigencia: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Póliza Resp. Civil - Aseguradora</label>
                    <input
                      type="text"
                      value={formData.poliza_resp_civil_aseguradora}
                      onChange={(e) => setFormData({ ...formData, poliza_resp_civil_aseguradora: e.target.value })}
                      placeholder="Ej: LA PREVISORA S.A."
                    />
                  </div>

                  {/* Revisión Técnico-Mecánica */}
                  <div className="form-group">
                    <label>Revisión Técnico-Mecánica - Número</label>
                    <input
                      type="text"
                      value={formData.tecnomecanica_numero}
                      onChange={(e) => setFormData({ ...formData, tecnomecanica_numero: e.target.value })}
                      placeholder="Ej: 179327556"
                    />
                  </div>
                  <div className="form-group">
                    <label>Revisión Técnico-Mecánica - Vigencia</label>
                    <input
                      type="date"
                      value={formData.tecnomecanica_vigencia}
                      onChange={(e) => setFormData({ ...formData, tecnomecanica_vigencia: e.target.value })}
                    />
                  </div>

                  {/* Tarjeta de Propiedad */}
                  <div className="form-group">
                    <label>Tarjeta de Propiedad - Número</label>
                    <input
                      type="text"
                      value={formData.tarjeta_propiedad_numero}
                      onChange={(e) => setFormData({ ...formData, tarjeta_propiedad_numero: e.target.value })}
                      placeholder="Número de tarjeta de propiedad"
                    />
                  </div>
                  <div className="form-group">
                    <label>Tarjeta de Propiedad - Vigencia</label>
                    <input
                      type="date"
                      value={formData.tarjeta_propiedad_vigencia}
                      onChange={(e) => setFormData({ ...formData, tarjeta_propiedad_vigencia: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={guardarVehiculo} className="btn-primary">
                💾 Guardar
              </button>
              <button onClick={() => {
                setShowCrearModal(false);
                setShowEditarModal(false);
              }} className="btn-secondary">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ver */}
      {showVerModal && vehiculoSeleccionado && (
        <div className="modal-overlay" onClick={() => setShowVerModal(false)}>
          <div className="modal-content" style={{ maxWidth: '1000px', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detalles del Vehículo - {vehiculoSeleccionado.placa}</h2>
              <button className="modal-close" onClick={() => setShowVerModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: 'calc(90vh - 120px)', overflowY: 'auto' }}>
              {/* Alertas de vencimiento */}
              {documentosVehiculo.length > 0 && (() => {
                const documentosVencidos = documentosVehiculo.filter(doc => {
                  const estado = calcularEstadoVencimiento(doc.fecha_vencimiento);
                  return estado.estado === 'vencido' || estado.estado === 'por_vencer';
                });
                
                if (documentosVencidos.length > 0) {
                  return (
                    <div style={{ 
                      marginBottom: '20px', 
                      padding: '15px', 
                      borderRadius: '8px',
                      background: '#fee2e2',
                      border: '2px solid #dc3545'
                    }}>
                      <h3 style={{ margin: '0 0 10px 0', color: '#dc3545', fontSize: '1.1em' }}>
                        ⚠️ ALERTAS DE VENCIMIENTO
                      </h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {documentosVencidos.map((doc) => {
                          const estado = calcularEstadoVencimiento(doc.fecha_vencimiento);
                          return (
                            <div key={doc.id} style={{
                              padding: '10px',
                              borderRadius: '6px',
                              background: estado.bgColor,
                              border: `2px solid ${estado.color}`,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <div>
                                <strong style={{ color: estado.color }}>{doc.tipo_documento_nombre}</strong>
                                {doc.numero_documento && <div style={{ fontSize: '0.9em', marginTop: '4px' }}>N°: {doc.numero_documento}</div>}
                              </div>
                              <div style={{ 
                                padding: '6px 12px', 
                                borderRadius: '4px',
                                background: estado.color,
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.9em'
                              }}>
                                {estado.mensaje}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Información del Vehículo */}
              <div className="form-section" style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '15px', color: '#2c3e50', borderBottom: '2px solid #0f4222', paddingBottom: '10px' }}>
                  📋 INFORMACIÓN DEL VEHÍCULO
                </h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Placa</label>
                    <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                      <strong style={{ color: '#0f4222', fontSize: '1.1em' }}>{vehiculoSeleccionado.placa}</strong>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Número Interno</label>
                    <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                      {vehiculoSeleccionado.numero_interno || '-'}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Marca</label>
                    <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                      {vehiculoSeleccionado.marca || '-'}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Modelo</label>
                    <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                      {vehiculoSeleccionado.modelo || '-'}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Año</label>
                    <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                      {vehiculoSeleccionado.año || '-'}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Color</label>
                    <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                      {vehiculoSeleccionado.color || '-'}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Tipo de Vehículo</label>
                    <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                      {vehiculoSeleccionado.tipo_vehiculo || '-'}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Repotenciado</label>
                    <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                      {vehiculoSeleccionado.repotenciado ? 'Sí' : 'No'}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Configuración</label>
                    <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                      {vehiculoSeleccionado.configuracion || '-'}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Capacidad de Carga (kg)</label>
                    <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                      {vehiculoSeleccionado.capacidad_carga || '-'}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Carrocería</label>
                    <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                      {vehiculoSeleccionado.carroceria || '-'}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Peso Vacío (kg)</label>
                    <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                      {vehiculoSeleccionado.peso_vacio || '-'}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Número de Motor</label>
                    <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                      {vehiculoSeleccionado.numero_motor || '-'}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Número de Chasis</label>
                    <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                      {vehiculoSeleccionado.numero_chasis || '-'}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Tipo de Combustible</label>
                    <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                      {vehiculoSeleccionado.tipo_combustible || '-'}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Capacidad del Tanque (Litros)</label>
                    <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                      {vehiculoSeleccionado.capacidad_tanque || '-'}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Estado</label>
                    <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                      {vehiculoSeleccionado.estado === 'activo' ? (
                        <span className="badge badge-success">✅ Activo</span>
                      ) : vehiculoSeleccionado.estado === 'inactivo' ? (
                        <span className="badge badge-secondary">⏸️ Inactivo</span>
                      ) : vehiculoSeleccionado.estado === 'mantenimiento' ? (
                        <span className="badge badge-warning">🔧 Mantenimiento</span>
                      ) : vehiculoSeleccionado.estado === 'baja' ? (
                        <span className="badge badge-danger">❌ Baja</span>
                      ) : (
                        vehiculoSeleccionado.estado
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Operador Asignado</label>
                    <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                      {vehiculoSeleccionado.operador_nombre ? (
                        <div>
                          <strong>{vehiculoSeleccionado.operador_nombre}</strong>
                          {vehiculoSeleccionado.operador_documento && (
                            <div style={{ fontSize: '0.9em', color: '#666', marginTop: '4px' }}>
                              CC: {vehiculoSeleccionado.operador_documento}
                            </div>
                          )}
                        </div>
                      ) : (
                        'Sin asignar'
                      )}
                    </div>
                  </div>
                  {vehiculoSeleccionado.propietario_nombre && (
                    <div className="form-group">
                      <label>Propietario</label>
                      <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                        {vehiculoSeleccionado.propietario_nombre}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Documentos y Pólizas del Formulario */}
              {vehiculoSeleccionado.documentos_vehiculo && (
                <div className="form-section" style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '15px', color: '#2c3e50', borderBottom: '2px solid #0f4222', paddingBottom: '10px' }}>
                    📋 DOCUMENTOS Y PÓLIZAS (Desde Postulación)
                  </h3>
                  <div className="form-grid">
                    {/* SOAT */}
                    <div className="form-group">
                      <label>SOAT - Número</label>
                      <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                        {vehiculoSeleccionado.documentos_vehiculo.soat_numero || '-'}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>SOAT - Vigencia</label>
                      <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                        {vehiculoSeleccionado.documentos_vehiculo.soat_vigencia 
                          ? (() => {
                              const fecha = new Date(vehiculoSeleccionado.documentos_vehiculo.soat_vigencia);
                              const estado = calcularEstadoVencimiento(vehiculoSeleccionado.documentos_vehiculo.soat_vigencia);
                              return (
                                <>
                                  <div>{fecha.toLocaleDateString('es-CO')}</div>
                                  <div style={{ 
                                    fontSize: '0.85em', 
                                    color: estado.color,
                                    fontWeight: 'bold',
                                    marginTop: '4px'
                                  }}>
                                    {estado.mensaje}
                                  </div>
                                </>
                              );
                            })()
                          : '-'}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>SOAT - Aseguradora</label>
                      <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                        {vehiculoSeleccionado.documentos_vehiculo.soat_aseguradora || '-'}
                      </div>
                    </div>

                    {/* Póliza Responsabilidad Civil */}
                    <div className="form-group">
                      <label>Póliza Resp. Civil - Número</label>
                      <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                        {vehiculoSeleccionado.documentos_vehiculo.poliza_resp_civil_numero || '-'}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Póliza Resp. Civil - Vigencia</label>
                      <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                        {vehiculoSeleccionado.documentos_vehiculo.poliza_resp_civil_vigencia 
                          ? (() => {
                              const fecha = new Date(vehiculoSeleccionado.documentos_vehiculo.poliza_resp_civil_vigencia);
                              const estado = calcularEstadoVencimiento(vehiculoSeleccionado.documentos_vehiculo.poliza_resp_civil_vigencia);
                              return (
                                <>
                                  <div>{fecha.toLocaleDateString('es-CO')}</div>
                                  <div style={{ 
                                    fontSize: '0.85em', 
                                    color: estado.color,
                                    fontWeight: 'bold',
                                    marginTop: '4px'
                                  }}>
                                    {estado.mensaje}
                                  </div>
                                </>
                              );
                            })()
                          : '-'}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Póliza Resp. Civil - Aseguradora</label>
                      <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                        {vehiculoSeleccionado.documentos_vehiculo.poliza_resp_civil_aseguradora || '-'}
                      </div>
                    </div>

                    {/* Revisión Técnico-Mecánica */}
                    <div className="form-group">
                      <label>Revisión Técnico-Mecánica - Número</label>
                      <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                        {vehiculoSeleccionado.documentos_vehiculo.tecnomecanica_numero || '-'}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Revisión Técnico-Mecánica - Vigencia</label>
                      <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                        {vehiculoSeleccionado.documentos_vehiculo.tecnomecanica_vigencia 
                          ? (() => {
                              const fecha = new Date(vehiculoSeleccionado.documentos_vehiculo.tecnomecanica_vigencia);
                              const estado = calcularEstadoVencimiento(vehiculoSeleccionado.documentos_vehiculo.tecnomecanica_vigencia);
                              return (
                                <>
                                  <div>{fecha.toLocaleDateString('es-CO')}</div>
                                  <div style={{ 
                                    fontSize: '0.85em', 
                                    color: estado.color,
                                    fontWeight: 'bold',
                                    marginTop: '4px'
                                  }}>
                                    {estado.mensaje}
                                  </div>
                                </>
                              );
                            })()
                          : '-'}
                      </div>
                    </div>

                    {/* Tarjeta de Propiedad */}
                    <div className="form-group">
                      <label>Tarjeta de Propiedad - Número</label>
                      <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                        {vehiculoSeleccionado.documentos_vehiculo.tarjeta_propiedad_numero || '-'}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Tarjeta de Propiedad - Vigencia</label>
                      <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
                        {vehiculoSeleccionado.documentos_vehiculo.tarjeta_propiedad_vigencia 
                          ? (() => {
                              const fecha = new Date(vehiculoSeleccionado.documentos_vehiculo.tarjeta_propiedad_vigencia);
                              const estado = calcularEstadoVencimiento(vehiculoSeleccionado.documentos_vehiculo.tarjeta_propiedad_vigencia);
                              return (
                                <>
                                  <div>{fecha.toLocaleDateString('es-CO')}</div>
                                  <div style={{ 
                                    fontSize: '0.85em', 
                                    color: estado.color,
                                    fontWeight: 'bold',
                                    marginTop: '4px'
                                  }}>
                                    {estado.mensaje}
                                  </div>
                                </>
                              );
                            })()
                          : '-'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Documentos y Pólizas */}
              <div className="form-section">
                <h3 style={{ marginBottom: '15px', color: '#2c3e50', borderBottom: '2px solid #0f4222', paddingBottom: '10px' }}>
                  📄 DOCUMENTOS Y PÓLIZAS (Subidos como Archivos)
                </h3>
                {loadingDocumentos ? (
                  <div className="loading">Cargando documentos...</div>
                ) : documentosVehiculo.length === 0 ? (
                  <div className="empty-state">
                    <p>No hay documentos registrados para este vehículo.</p>
                  </div>
                ) : (
                  <div className="table-container" style={{ marginTop: '15px' }}>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Tipo de Documento</th>
                          <th>Número</th>
                          <th>Fecha Emisión</th>
                          <th>Fecha Vencimiento</th>
                          <th>Estado</th>
                          <th>Verificación</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documentosVehiculo.map((doc) => {
                          const estadoVenc = calcularEstadoVencimiento(doc.fecha_vencimiento);
                          return (
                            <tr 
                              key={doc.id}
                              style={{
                                background: (estadoVenc.estado === 'vencido' || estadoVenc.estado === 'por_vencer') 
                                  ? estadoVenc.bgColor 
                                  : 'transparent'
                              }}
                            >
                              <td>
                                <strong>{doc.tipo_documento_nombre || 'Documento'}</strong>
                              </td>
                              <td>{doc.numero_documento || '-'}</td>
                              <td>
                                {doc.fecha_emision 
                                  ? new Date(doc.fecha_emision).toLocaleDateString('es-CO')
                                  : '-'}
                              </td>
                              <td>
                                {doc.fecha_vencimiento ? (
                                  <div>
                                    <div>{new Date(doc.fecha_vencimiento).toLocaleDateString('es-CO')}</div>
                                    <div style={{ 
                                      fontSize: '0.85em', 
                                      color: estadoVenc.color,
                                      fontWeight: 'bold',
                                      marginTop: '4px'
                                    }}>
                                      {estadoVenc.mensaje}
                                    </div>
                                  </div>
                                ) : '-'}
                              </td>
                              <td>
                                {estadoVenc.estado === 'vencido' ? (
                                  <span className="badge badge-danger">❌ Vencido</span>
                                ) : estadoVenc.estado === 'por_vencer' ? (
                                  <span className="badge badge-warning">⚠️ Por Vencer</span>
                                ) : estadoVenc.estado === 'vigente' ? (
                                  <span className="badge badge-success">✅ Vigente</span>
                                ) : (
                                  <span className="badge badge-secondary">Sin fecha</span>
                                )}
                              </td>
                              <td>
                                {doc.resultado_verificacion === 'aprobado' ? (
                                  <span className="badge badge-success">✓ Aprobado</span>
                                ) : doc.resultado_verificacion === 'rechazado' ? (
                                  <span className="badge badge-danger">✗ Rechazado</span>
                                ) : (
                                  <span className="badge badge-warning">⏳ Pendiente</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowVerModal(false)} className="btn-secondary">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Asignar */}
      {showAsignarModal && vehiculoSeleccionado && (
        <div className="modal-overlay" onClick={() => setShowAsignarModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Asignar Vehículo {vehiculoSeleccionado.placa}</h2>
              <button onClick={() => setShowAsignarModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Operador</label>
                <select
                  value={operadorSeleccionado}
                  onChange={(e) => setOperadorSeleccionado(e.target.value)}
                >
                  <option value="">Sin asignar</option>
                  {operadores.map((op) => (
                    <option key={op.id} value={op.id.toString()}>
                      {op.nombres} {op.apellidos} - {op.numero_documento}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={asignarVehiculo} className="btn-primary">
                💾 Guardar
              </button>
              <button onClick={() => setShowAsignarModal(false)} className="btn-secondary">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vehiculos;

